import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ImageWithFallback } from './figma/ImageWithFallback';

interface SmartImageProps {
  src: string;
  alt: string;
  className?: string;
  containerClassName?: string;
  sizes?: string;
  loading?: 'lazy' | 'eager';
  mode?: 'auto' | 'always-blur' | 'never-blur';
  blurIntensity?: 'subtle' | 'normal' | 'strong';
  onImageAnalyzed?: (metadata: ImageMetadata) => void;
}

interface ImageMetadata {
  width: number;
  height: number;
  aspectRatio: number;
  needsBlur: boolean;
  type: 'small' | 'panoramic' | 'square' | 'portrait' | 'landscape' | 'normal';
}

// Cache para análises de imagens
const imageAnalysisCache = new Map<string, ImageMetadata>();

export function SmartImage({
  src,
  alt,
  className = '',
  containerClassName = '',
  sizes = '100vw',
  loading = 'lazy',
  mode = 'auto',
  blurIntensity = 'normal',
  onImageAnalyzed
}: SmartImageProps) {
  const [imageMetadata, setImageMetadata] = useState<ImageMetadata | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(true);
  const onImageAnalyzedRef = useRef(onImageAnalyzed);
  
  // Manter referência atualizada do callback sem triggerar re-renders
  useEffect(() => {
    onImageAnalyzedRef.current = onImageAnalyzed;
  }, [onImageAnalyzed]);

  // Memoize da função de análise para evitar re-criações
  const analyzeImage = useMemo(() => {
    return (imageSrc: string): Promise<ImageMetadata> => {
      // Verificar cache primeiro
      if (imageAnalysisCache.has(imageSrc)) {
        return Promise.resolve(imageAnalysisCache.get(imageSrc)!);
      }

      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const { naturalWidth: width, naturalHeight: height } = img;
          const aspectRatio = width / height;
          
          // Determine image characteristics
          const isSmall = width < 800 || height < 600;
          const isPanoramic = aspectRatio > 3 || aspectRatio < 0.4;
          const isSquare = Math.abs(aspectRatio - 1) < 0.2;
          const isPortrait = aspectRatio < 0.8;
          const isLandscape = aspectRatio > 1.5 && aspectRatio <= 3;
          
          // Determine if needs blur background
          const needsBlur = isSmall || isPanoramic;
          
          let type: ImageMetadata['type'] = 'normal';
          if (isSmall) type = 'small';
          else if (isPanoramic) type = 'panoramic';
          else if (isSquare) type = 'square';
          else if (isPortrait) type = 'portrait';
          else if (isLandscape) type = 'landscape';
          
          const metadata = { width, height, aspectRatio, needsBlur, type };
          
          // Cache o resultado
          imageAnalysisCache.set(imageSrc, metadata);
          resolve(metadata);
        };
        
        img.onerror = () => {
          // Fallback for failed images
          const metadata = { 
            width: 800, 
            height: 600, 
            aspectRatio: 1.33, 
            needsBlur: false, 
            type: 'normal' as const 
          };
          imageAnalysisCache.set(imageSrc, metadata);
          resolve(metadata);
        };
        
        img.src = imageSrc;
      });
    };
  }, []);

  useEffect(() => {
    if (!src || mode === 'never-blur') {
      setIsAnalyzing(false);
      return;
    }

    // Verificar cache primeiro
    if (imageAnalysisCache.has(src)) {
      const cachedMetadata = imageAnalysisCache.get(src)!;
      setImageMetadata(cachedMetadata);
      setIsAnalyzing(false);
      
      if (onImageAnalyzedRef.current) {
        onImageAnalyzedRef.current(cachedMetadata);
      }
      return;
    }

    setIsAnalyzing(true);
    
    analyzeImage(src).then((metadata) => {
      setImageMetadata(metadata);
      setIsAnalyzing(false);
      
      if (onImageAnalyzedRef.current) {
        onImageAnalyzedRef.current(metadata);
      }
    });
  }, [src, mode, analyzeImage]);

  // Memoize blur determination
  const shouldUseBlur = useMemo(() => {
    if (mode === 'always-blur') return true;
    if (mode === 'never-blur') return false;
    return imageMetadata?.needsBlur ?? false;
  }, [mode, imageMetadata?.needsBlur]);

  // Memoize blur filter values
  const blurFilter = useMemo(() => {
    switch (blurIntensity) {
      case 'subtle': return 'blur(15px) brightness(0.8) saturate(1.1)';
      case 'strong': return 'blur(25px) brightness(0.6) saturate(1.3)';
      default: return 'blur(20px) brightness(0.7) saturate(1.2)';
    }
  }, [blurIntensity]);

  // Loading state
  if (isAnalyzing && mode === 'auto') {
    return (
      <div className={`w-full h-full bg-muted animate-pulse ${containerClassName}`}>
        <div className="w-full h-full flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-orange-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (shouldUseBlur) {
    return (
      <div 
        className={`relative overflow-hidden w-full h-full ${containerClassName}`}
        style={{
          transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        {/* Blur background */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${src})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: blurFilter,
            transform: 'scale(1.1)',
            top: '-10%',
            left: '-10%',
            right: '-10%',
            bottom: '-10%',
            zIndex: 1
          }}
        />
        
        {/* Main image content */}
        <div 
          className="relative flex items-center justify-center h-full w-full"
          style={{ zIndex: 2 }}
        >
          <ImageWithFallback
            src={src}
            alt={alt}
            className={`object-contain filter brightness-105 contrast-110 saturate-105 rounded-lg shadow-2xl ${className}`}
            style={{
              maxWidth: imageMetadata?.type === 'square' ? '60%' : 
                       imageMetadata?.type === 'portrait' ? '60%' :
                       imageMetadata?.type === 'landscape' ? '85%' : '80%',
              maxHeight: imageMetadata?.type === 'square' ? '90%' : 
                        imageMetadata?.type === 'portrait' ? '85%' :
                        imageMetadata?.type === 'landscape' ? '70%' : '80%',
              transition: 'transform 500ms cubic-bezier(0.4, 0, 0.2, 1), filter 300ms ease-out'
            }}
            sizes={sizes}
            loading={loading}
          />
        </div>
      </div>
    );
  }

  // Standard image display
  return (
    <div className={`w-full h-full ${containerClassName}`}>
      <ImageWithFallback
        src={src}
        alt={alt}
        className={`w-full h-full object-cover object-center filter brightness-101 contrast-105 saturate-102 ${className}`}
        style={{
          transition: 'transform 500ms ease-out, filter 300ms ease-out'
        }}
        sizes={sizes}
        loading={loading}
      />
    </div>
  );
}

// Hook for image analysis
export function useImageAnalysis(src: string) {
  const [metadata, setMetadata] = useState<ImageMetadata | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!src) return;

    // Verificar cache primeiro
    if (imageAnalysisCache.has(src)) {
      setMetadata(imageAnalysisCache.get(src)!);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    
    const img = new Image();
    img.onload = () => {
      const { naturalWidth: width, naturalHeight: height } = img;
      const aspectRatio = width / height;
      
      const isSmall = width < 800 || height < 600;
      const isPanoramic = aspectRatio > 3 || aspectRatio < 0.4;
      const isSquare = Math.abs(aspectRatio - 1) < 0.2;
      const isPortrait = aspectRatio < 0.8;
      const isLandscape = aspectRatio > 1.5 && aspectRatio <= 3;
      
      const needsBlur = isSmall || isPanoramic;
      
      let type: ImageMetadata['type'] = 'normal';
      if (isSmall) type = 'small';
      else if (isPanoramic) type = 'panoramic';
      else if (isSquare) type = 'square';
      else if (isPortrait) type = 'portrait';
      else if (isLandscape) type = 'landscape';
      
      const newMetadata = { width, height, aspectRatio, needsBlur, type };
      imageAnalysisCache.set(src, newMetadata);
      setMetadata(newMetadata);
      setIsLoading(false);
    };
    
    img.onerror = () => {
      const fallbackMetadata = { 
        width: 800, 
        height: 600, 
        aspectRatio: 1.33, 
        needsBlur: false, 
        type: 'normal' as const
      };
      imageAnalysisCache.set(src, fallbackMetadata);
      setMetadata(fallbackMetadata);
      setIsLoading(false);
    };
    
    img.src = src;
  }, [src]);

  return { metadata, isLoading };
}